import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../contexts/ThemeContext';
import Navigation from '../../components/feature/Navigation';
import RichTextEditor from '../../components/base/RichTextEditor';
import jsPDF from 'jspdf';

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  broker?: string;
  display_order?: number;
  user_id?: string;
  is_global?: boolean;
}

interface TextTemplate {
  id: string;
  name: string;
  body: string;
  broker?: string;
  display_order?: number;
  user_id?: string;
  is_global?: boolean;
}

export default function Templates() {
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [textTemplates, setTextTemplates] = useState<TextTemplate[]>([]);
  const [editingEmailId, setEditingEmailId] = useState<string | null>(null);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [showAddEmailForm, setShowAddEmailForm] = useState(false);
  const [showAddTextForm, setShowAddTextForm] = useState(false);
  const [showPagesDropdown, setShowPagesDropdown] = useState(false);
  const [brokerFilter, setBrokerFilter] = useState<string>('All');
  const [templateTypeFilter, setTemplateTypeFilter] = useState<string>('Both');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  
  // Add expanded state tracking
  const [expandedEmailTemplates, setExpandedEmailTemplates] = useState<Set<string>>(new Set());
  const [expandedTextTemplates, setExpandedTextTemplates] = useState<Set<string>>(new Set());
  const [allEmailExpanded, setAllEmailExpanded] = useState(false);
  const [allTextExpanded, setAllTextExpanded] = useState(false);

  const [newEmailTemplate, setNewEmailTemplate] = useState<Omit<EmailTemplate, 'id'>>({
    name: '',
    subject: '',
    body: '',
    broker: 'All'
  });

  const [newTextTemplate, setNewTextTemplate] = useState<Omit<TextTemplate, 'id'>>({
    name: '',
    body: '',
    broker: 'All'
  });

  // User profile state is already defined here
  const [userFullName, setUserFullName] = useState<string>('');

  // Check authentication - already loads user profile name
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
      } else {
        setCurrentUserId(session.user.id);
        // Load user profile name
        const metadata = session.user.user_metadata;
        const firstName = metadata.first_name || '';
        const lastName = metadata.last_name || '';
        setUserFullName(`${firstName} ${lastName}`.trim() || 'User');
      }
    };
    checkAuth();
  }, [navigate]);

  // Load templates
  useEffect(() => {
    if (currentUserId) {
      loadTemplates();
    }
  }, [currentUserId]);

  const loadTemplates = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Load email templates - user's personal templates only
    const { data: emailData, error: emailError } = await supabase
      .from('email_templates')
      .select('*')
      .eq('user_id', session.user.id)
      .order('display_order', { ascending: true, nullsFirst: false });

    if (emailError) {
      console.error('Error loading email templates:', emailError);
    }

    if (emailData) {
      // Assign display_order to templates that don't have one
      const templatesWithOrder = emailData.map((template, index) => ({
        ...template,
        display_order: template.display_order ?? index
      }));
      setEmailTemplates(templatesWithOrder);
    }

    // Load text templates - user's personal templates only
    const { data: textData, error: textError } = await supabase
      .from('text_templates')
      .select('*')
      .eq('user_id', session.user.id)
      .order('display_order', { ascending: true, nullsFirst: false });

    if (textError) {
      console.error('Error loading text templates:', textError);
    }

    if (textData) {
      // Assign display_order to templates that don't have one
      const templatesWithOrder = textData.map((template, index) => ({
        ...template,
        display_order: template.display_order ?? index
      }));
      setTextTemplates(templatesWithOrder);
    }
  };

  const generatePreApprovalPDF = (template: EmailTemplate, clientData?: any) => {
    const doc = new jsPDF();
    
    // Set up the document
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let yPosition = 20;

    // Add letterhead/header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('MORTGAGE PRE-APPROVAL LETTER', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Add date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const today = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    doc.text(`Date: ${today}`, margin, yPosition);
    yPosition += 10;

    // Add a line separator
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    // Process the template body
    let body = template.body;
    
    // Replace variables with client data if provided
    if (clientData) {
      body = body.replace(/{clientName}/g, clientData.name || '[Client Name]');
      body = body.replace(/\${loanAmount}/g, clientData.loan_amount || '[Loan Amount]');
      body = body.replace(/\${purchasePrice}/g, clientData.purchase_price || '[Purchase Price]');
      body = body.replace(/{state}/g, clientData.state || '[State]');
      body = body.replace(/{loanType}/g, clientData.loan_type || '[Loan Type]');
      body = body.replace(/{brokerName}/g, '[Your Name]');
      body = body.replace(/{brokerEmail}/g, '[Your Email]');
    } else {
      // Use placeholder values
      body = body.replace(/{clientName}/g, '[Client Name]');
      body = body.replace(/\${loanAmount}/g, '[Loan Amount]');
      body = body.replace(/\${purchasePrice}/g, '[Purchase Price]');
      body = body.replace(/{state}/g, '[State]');
      body = body.replace(/{loanType}/g, '[Loan Type]');
      body = body.replace(/{brokerName}/g, '[Your Name]');
      body = body.replace(/{brokerEmail}/g, '[Your Email]');
    }

    // Remove HTML tags and format text
    body = body.replace(/<strong>/g, '').replace(/<\/strong>/g, '');
    body = body.replace(/<br>/g, '\n').replace(/<br\/>/g, '\n');
    
    // Split into paragraphs
    const paragraphs = body.split('\n\n');
    
    doc.setFontSize(11);
    
    paragraphs.forEach((paragraph) => {
      if (paragraph.trim()) {
        // Check if it's a heading (contains colon at the end)
        if (paragraph.trim().endsWith(':')) {
          doc.setFont('helvetica', 'bold');
          yPosition += 5;
        } else {
          doc.setFont('helvetica', 'normal');
        }
        
        // Split long paragraphs into lines
        const lines = doc.splitTextToSize(paragraph.trim(), contentWidth);
        
        // Check if we need a new page
        if (yPosition + (lines.length * 7) > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          yPosition = margin;
        }
        
        lines.forEach((line: string) => {
          doc.text(line, margin, yPosition);
          yPosition += 7;
        });
        
        yPosition += 3;
      }
    });

    // Add footer
    yPosition += 10;
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 7;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.text('This pre-approval letter is issued subject to final underwriting approval.', pageWidth / 2, yPosition, { align: 'center' });

    // Save the PDF
    const fileName = clientData 
      ? `Pre-Approval-${clientData.name.replace(/\s+/g, '-')}-${today.replace(/\s+/g, '-')}.pdf`
      : `Pre-Approval-Letter-${today.replace(/\s+/g, '-')}.pdf`;
    
    doc.save(fileName);
  };

  const handleEditEmail = (id: string) => {
    setEditingEmailId(id);
  };

  const handleEditText = (id: string) => {
    setEditingTextId(id);
  };

  const handleSaveEmail = async (id: string) => {
    const template = emailTemplates.find(t => t.id === id);
    if (!template) return;

    const { error } = await supabase
      .from('email_templates')
      .update({
        name: template.name,
        subject: template.subject,
        body: template.body,
        broker: template.broker || 'All'
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating email template:', error);
      alert('Failed to update template');
      return;
    }

    setEditingEmailId(null);
  };

  const handleSaveText = async (id: string) => {
    const template = textTemplates.find(t => t.id === id);
    if (!template) return;

    const { error } = await supabase
      .from('text_templates')
      .update({
        name: template.name,
        body: template.body,
        broker: template.broker || 'All'
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating text template:', error);
      alert('Failed to update template');
      return;
    }

    setEditingTextId(null);
  };

  const handleChangeEmail = (id: string, field: keyof EmailTemplate, value: string) => {
    setEmailTemplates(emailTemplates.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  const handleChangeText = (id: string, field: keyof TextTemplate, value: string) => {
    setTextTemplates(textTemplates.map(t => 
      t.id === id ? { ...t, [field]: value } : t
    ));
  };

  const handleDeleteEmail = async (id: string) => {
    if (confirm('Are you sure you want to delete this email template?')) {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting email template:', error);
        alert('Failed to delete template');
        return;
      }

      setEmailTemplates(emailTemplates.filter(t => t.id !== id));
    }
  };

  const handleDeleteText = async (id: string) => {
    if (confirm('Are you sure you want to delete this text template?')) {
      const { error } = await supabase
        .from('text_templates')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting text template:', error);
        alert('Failed to delete template');
        return;
      }

      setTextTemplates(textTemplates.filter(t => t.id !== id));
    }
  };

  const handleAddEmailTemplate = async () => {
    if (newEmailTemplate.name && newEmailTemplate.subject && newEmailTemplate.body) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get max display order
      const { data: maxOrderData } = await supabase
        .from('email_templates')
        .select('display_order')
        .eq('user_id', session.user.id)
        .order('display_order', { ascending: false })
        .limit(1);

      const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

      const { data, error } = await supabase
        .from('email_templates')
        .insert([{
          name: newEmailTemplate.name,
          subject: newEmailTemplate.subject,
          body: newEmailTemplate.body,
          broker: newEmailTemplate.broker || 'All',
          user_id: session.user.id,
          display_order: maxOrder + 1
        }])
        .select()
        .single();

      if (error) {
        console.error('Error adding email template:', error);
        alert('Failed to add template');
        return;
      }

      if (data) {
        setEmailTemplates([...emailTemplates, data]);
      }

      setNewEmailTemplate({ name: '', subject: '', body: '', broker: 'All' });
      setShowAddEmailForm(false);
    }
  };

  const handleAddTextTemplate = async () => {
    if (newTextTemplate.name && newTextTemplate.body) {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get max display order
      const { data: maxOrderData } = await supabase
        .from('text_templates')
        .select('display_order')
        .eq('user_id', session.user.id)
        .order('display_order', { ascending: false })
        .limit(1);

      const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;

      const { data, error } = await supabase
        .from('text_templates')
        .insert([{
          name: newTextTemplate.name,
          body: newTextTemplate.body,
          broker: newTextTemplate.broker || 'All',
          user_id: session.user.id,
          display_order: maxOrder + 1
        }])
        .select()
        .single();

      if (error) {
        console.error('Error adding text template:', error);
        alert('Failed to add template');
        return;
      }

      if (data) {
        setTextTemplates([...textTemplates, data]);
      }

      setNewTextTemplate({ name: '', body: '', broker: 'All' });
      setShowAddTextForm(false);
    }
  };

  const moveTemplate = async (id: string, direction: 'up' | 'down', type: 'email' | 'text') => {
    const templates = type === 'email' ? emailTemplates : textTemplates;
    const setTemplates = type === 'email' ? setEmailTemplates : setTextTemplates;
    const tableName = type === 'email' ? 'email_templates' : 'text_templates';

    const currentIndex = templates.findIndex(t => t.id === id);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= templates.length) return;

    // Create new array with swapped positions
    const newTemplates = [...templates];
    [newTemplates[currentIndex], newTemplates[newIndex]] = [newTemplates[newIndex], newTemplates[currentIndex]];

    // Update display_order for both templates
    const updates = [
      {
        id: newTemplates[currentIndex].id,
        display_order: currentIndex
      },
      {
        id: newTemplates[newIndex].id,
        display_order: newIndex
      }
    ];

    // Update in database
    for (const update of updates) {
      const { error } = await supabase
        .from(tableName)
        .update({ display_order: update.display_order })
        .eq('id', update.id);

      if (error) {
        console.error('Error updating template order:', error);
        return;
      }
    }

    // Update local state with new display_order values
    newTemplates[currentIndex].display_order = currentIndex;
    newTemplates[newIndex].display_order = newIndex;
    setTemplates(newTemplates);
  };

  const handleMoveEmailTemplate = async (id: string, direction: 'up' | 'down') => {
    moveTemplate(id, direction, 'email');
  };

  const handleMoveTextTemplate = async (id: string, direction: 'up' | 'down') => {
    moveTemplate(id, direction, 'text');
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const getBrokerBadgeColor = (broker: string) => {
    switch (broker) {
      case 'NS':
        return 'bg-teal/10 text-teal';
      case 'TM':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getOwnershipBadge = (template: EmailTemplate | TextTemplate) => {
    // All templates on this page are personal (user's own)
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        <i className="ri-user-line mr-1"></i>
        My Template
      </span>
    );
  };

  // Filter templates based on broker filter
  const filteredEmailTemplates = emailTemplates.filter(template => {
    if (brokerFilter === 'All') return true;
    return template.broker === brokerFilter || template.broker === 'All';
  });

  const filteredTextTemplates = textTemplates.filter(template => {
    if (brokerFilter === 'All') return true;
    return template.broker === brokerFilter || template.broker === 'All';
  });

  // Determine which sections to show based on template type filter
  const showEmailTemplates = templateTypeFilter === 'Both' || templateTypeFilter === 'Email';
  const showTextTemplates = templateTypeFilter === 'Both' || templateTypeFilter === 'Text';

  const toggleEmailTemplate = (id: string) => {
    const newExpanded = new Set(expandedEmailTemplates);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedEmailTemplates(newExpanded);
  };

  const toggleTextTemplate = (id: string) => {
    const newExpanded = new Set(expandedTextTemplates);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedTextTemplates(newExpanded);
  };

  const expandAllEmailTemplates = () => {
    const allIds = new Set(filteredEmailTemplates.map(t => t.id));
    setExpandedEmailTemplates(allIds);
    setAllEmailExpanded(true);
  };

  const collapseAllEmailTemplates = () => {
    setExpandedEmailTemplates(new Set());
    setAllEmailExpanded(false);
  };

  const expandAllTextTemplates = () => {
    const allIds = new Set(filteredTextTemplates.map(t => t.id));
    setExpandedTextTemplates(allIds);
    setAllTextExpanded(true);
  };

  const collapseAllTextTemplates = () => {
    setExpandedTextTemplates(new Set());
    setAllTextExpanded(false);
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      {/* Navigation */}
      <Navigation currentPage="templates" userName={userFullName} />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} mb-2`}>My Templates</h1>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manage your personal email and text message templates</p>
        </div>

        {/* Info Banner */}
        <div className={`${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'} border rounded-xl p-4 mb-6`}>
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
            <div>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <strong className={isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}>Personal Templates:</strong> All templates shown here are your personal templates. Only you can see and use them. They won't be visible to other users.
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-4 mb-6`}>
          <div className="flex items-center gap-4">
            <label className={`text-sm font-semibold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} whitespace-nowrap`}>Filter by Broker:</label>
            <select
              value={brokerFilter}
              onChange={(e) => setBrokerFilter(e.target.value)}
              className={`px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
            >
              <option value="All">All Brokers</option>
              <option value="NS">NS (Next Step Lending)</option>
              <option value="TM">TM (Trademark Financing)</option>
            </select>
            
            <label className={`text-sm font-semibold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} whitespace-nowrap ml-4`}>Template Type:</label>
            <select
              value={templateTypeFilter}
              onChange={(e) => setTemplateTypeFilter(e.target.value)}
              className={`px-4 py-2 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal text-sm cursor-pointer`}
            >
              <option value="Both">Both</option>
              <option value="Email">Email Templates</option>
              <option value="Text">Text Templates</option>
            </select>
            
            {(brokerFilter !== 'All' || templateTypeFilter !== 'Both') && (
              <button
                onClick={() => {
                  setBrokerFilter('All');
                  setTemplateTypeFilter('Both');
                }}
                className="text-sm text-teal hover:text-teal/80 font-medium whitespace-nowrap cursor-pointer"
              >
                Clear Filters
              </button>
            )}
            <div className={`ml-auto text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {showEmailTemplates && showTextTemplates && (
                <>Showing {filteredEmailTemplates.length} email, {filteredTextTemplates.length} text</>
              )}
              {showEmailTemplates && !showTextTemplates && (
                <>Showing {filteredEmailTemplates.length} email templates</>
              )}
              {!showEmailTemplates && showTextTemplates && (
                <>Showing {filteredTextTemplates.length} text templates</>
              )}
            </div>
          </div>
        </div>

        {/* Template Variables Reference */}
        <div className={`${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'} border rounded-xl p-6 mb-8`}>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
            <i className="ri-code-s-slash-line text-blue-600"></i>
            Available Template Variables
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-blue-100'} rounded-lg p-4 border`}>
              <code className="text-sm font-mono text-blue-600">{'{clientName}'}</code>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Client's full name</p>
            </div>
            <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-blue-100'} rounded-lg p-4 border`}>
              <code className="text-sm font-mono text-blue-600">{'{firstName}'}</code>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Client's first name</p>
            </div>
            <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-blue-100'} rounded-lg p-4 border`}>
              <code className="text-sm font-mono text-blue-600">{'{lastName}'}</code>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Client's last name</p>
            </div>
            <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-blue-100'} rounded-lg p-4 border`}>
              <code className="text-sm font-mono text-blue-600">{'{state}'}</code>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Client's state</p>
            </div>
            <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-blue-100'} rounded-lg p-4 border`}>
              <code className="text-sm font-mono text-blue-600">{'{purchasePrice}'}</code>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Property purchase price</p>
            </div>
            <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-blue-100'} rounded-lg p-4 border`}>
              <code className="text-sm font-mono text-blue-600">{'{loanAmount}'}</code>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Loan amount</p>
            </div>
            <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-blue-100'} rounded-lg p-4 border`}>
              <code className="text-sm font-mono text-blue-600">{'{brokerName}'}</code>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Broker's full name (e.g., "Next Step Lending")</p>
            </div>
            <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-blue-100'} rounded-lg p-4 border`}>
              <code className="text-sm font-mono text-blue-600">{'{brokerEmail}'}</code>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1`}>Broker's email address</p>
            </div>
          </div>
          <div className={`mt-4 p-4 ${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-blue-100'} rounded-lg border`}>
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <strong className={isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}>💡 Tip:</strong> Copy and paste these variables into your templates. They will be automatically replaced with actual client data when you send emails or texts.
            </p>
          </div>
        </div>

        {/* Email Templates Section */}
        {showEmailTemplates && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`}>Email Templates</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={allEmailExpanded ? collapseAllEmailTemplates : expandAllEmailTemplates}
                  className={`${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-4 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 text-sm`}
                >
                  <i className={`${allEmailExpanded ? 'ri-subtract-line' : 'ri-add-line'} text-lg`}></i>
                  {allEmailExpanded ? 'Collapse All' : 'Expand All'}
                </button>
                <button
                  onClick={() => setShowAddEmailForm(!showAddEmailForm)}
                  className="bg-teal text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-add-line text-lg"></i>
                  Add Email Template
                </button>
              </div>
            </div>

            {/* Add Email Template Form */}
            {showAddEmailForm && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mb-6`}
              >
                <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} mb-4`}>Add New Email Template</h3>
                <div className="space-y-4 mb-4">
                  <input
                    type="text"
                    placeholder="Template Name"
                    value={newEmailTemplate.name}
                    onChange={(e) => setNewEmailTemplate({ ...newEmailTemplate, name: e.target.value })}
                    className={`w-full px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb] placeholder-gray-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal`}
                  />
                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} mb-2`}>Broker</label>
                    <select
                      value={newEmailTemplate.broker}
                      onChange={(e) => setNewEmailTemplate({ ...newEmailTemplate, broker: e.target.value })}
                      className={`w-full px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal cursor-pointer`}
                    >
                      <option value="All">All Brokers</option>
                      <option value="NS">NS (Next Step Lending)</option>
                      <option value="TM">TM (Trademark Financing)</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} mb-2`}>Email Subject</label>
                    <RichTextEditor
                      value={newEmailTemplate.subject}
                      onChange={(value) => setNewEmailTemplate({ ...newEmailTemplate, subject: value })}
                      placeholder="Enter email subject..."
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} mb-2`}>Email Body</label>
                    <RichTextEditor
                      value={newEmailTemplate.body}
                      onChange={(value) => setNewEmailTemplate({ ...newEmailTemplate, body: value })}
                      placeholder="Enter email body..."
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleAddEmailTemplate}
                    className="bg-teal text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Save Template
                  </button>
                  <button
                    onClick={() => {
                      setShowAddEmailForm(false);
                      setNewEmailTemplate({ name: '', subject: '', body: '', broker: 'All' });
                    }}
                    className={`${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer`}
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}

            {/* Email Templates List */}
            <div className="space-y-4">
              {filteredEmailTemplates.map((template, index) => (
                <div key={template.id} className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
                  {editingEmailId === template.id ? (
                    <div className="space-y-4">
                      <input
                        type="text"
                        value={template.name}
                        onChange={(e) => handleChangeEmail(template.id, 'name', e.target.value)}
                        className={`w-full px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal font-semibold`}
                      />
                      <div>
                        <label className={`block text-sm font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} mb-2`}>Broker</label>
                        <select
                          value={template.broker || 'All'}
                          onChange={(e) => handleChangeEmail(template.id, 'broker', e.target.value)}
                          className={`w-full px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal cursor-pointer`}
                        >
                          <option value="All">All Brokers</option>
                          <option value="NS">NS (Next Step Lending)</option>
                          <option value="TM">TM (Trademark Financing)</option>
                        </select>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} mb-2`}>Email Subject</label>
                        <RichTextEditor
                          value={template.subject}
                          onChange={(value) => handleChangeEmail(template.id, 'subject', value)}
                          placeholder="Enter email subject..."
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} mb-2`}>Email Body</label>
                        <RichTextEditor
                          value={template.body}
                          onChange={(value) => handleChangeEmail(template.id, 'body', value)}
                          placeholder="Enter email body..."
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleSaveEmail(template.id)}
                          className="bg-green-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-check-line mr-2"></i>
                          Save
                        </button>
                        <button
                          onClick={() => setEditingEmailId(null)}
                          className={`${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3 flex-1">
                          <button
                            onClick={() => toggleEmailTemplate(template.id)}
                            className={`w-8 h-8 flex items-center justify-center ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} rounded-lg transition-colors cursor-pointer`}
                          >
                            <i className={`${expandedEmailTemplates.has(template.id) ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                          </button>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`}>{template.name}</h3>
                              {getOwnershipBadge(template)}
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBrokerBadgeColor(template.broker || 'All')}`}>
                                {template.broker === 'NS' ? 'NS' : template.broker === 'TM' ? 'TM' : 'All Brokers'}
                              </span>
                            </div>
                            {expandedEmailTemplates.has(template.id) && (
                              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                <strong>Subject:</strong> <span dangerouslySetInnerHTML={{ __html: template.subject }} />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleMoveEmailTemplate(template.id, 'up')}
                            disabled={index === 0}
                            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                              index === 0 
                                ? `${isDarkMode ? 'bg-gray-800 text-gray-600' : 'bg-gray-100 text-gray-300'} cursor-not-allowed` 
                                : `${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`
                            }`}
                            title="Move Up"
                          >
                            <i className="ri-arrow-up-s-line"></i>
                          </button>
                          <button
                            onClick={() => handleMoveEmailTemplate(template.id, 'down')}
                            disabled={index === filteredEmailTemplates.length - 1}
                            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                              index === filteredEmailTemplates.length - 1
                                ? `${isDarkMode ? 'bg-gray-800 text-gray-600' : 'bg-gray-100 text-gray-300'} cursor-not-allowed` 
                                : `${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`
                            }`}
                            title="Move Down"
                          >
                            <i className="ri-arrow-down-s-line"></i>
                          </button>
                          <button
                            onClick={() => handleEditEmail(template.id)}
                            className="w-9 h-9 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                            title="Edit"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteEmail(template.id)}
                            className="w-9 h-9 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                      {expandedEmailTemplates.has(template.id) && (
                        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-4`}>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} [&_p]:mb-3 [&_p:last-child]:mb-0 [&_br]:block [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_strong]:font-semibold`} dangerouslySetInnerHTML={{ __html: template.body }} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              {filteredEmailTemplates.length === 0 && (
                <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-8 text-center`}>
                  <i className={`ri-inbox-line text-4xl ${isDarkMode ? 'text-gray-600' : 'text-gray-400'} mb-2`}></i>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>No email templates found for this filter</p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} mt-2`}>Create your first template to get started!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Text Templates Section */}
        {showTextTemplates && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`}>Text Message Templates</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={allTextExpanded ? collapseAllTextTemplates : expandAllTextTemplates}
                  className={`${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-4 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 text-sm`}
                >
                  <i className={`${allTextExpanded ? 'ri-subtract-line' : 'ri-add-line'} text-lg`}></i>
                  {allTextExpanded ? 'Collapse All' : 'Expand All'}
                </button>
                <button
                  onClick={() => setShowAddTextForm(!showAddTextForm)}
                  className="bg-teal text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                >
                  <i className="ri-add-line text-lg"></i>
                  Add Text Template
                </button>
              </div>
            </div>

            {/* Add Text Template Form */}
            {showAddTextForm && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6 mb-6`}
              >
                <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'} mb-4`}>Add New Text Template</h3>
                <div className="space-y-4 mb-4">
                  <input
                    type="text"
                    placeholder="Template Name"
                    value={newTextTemplate.name}
                    onChange={(e) => setNewTextTemplate({ ...newTextTemplate, name: e.target.value })}
                    className={`w-full px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb] placeholder-gray-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal`}
                  />
                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} mb-2`}>Broker</label>
                    <select
                      value={newTextTemplate.broker}
                      onChange={(e) => setNewTextTemplate({ ...newTextTemplate, broker: e.target.value })}
                      className={`w-full px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal cursor-pointer`}
                    >
                      <option value="All">All Brokers</option>
                      <option value="NS">NS (Next Step Lending)</option>
                      <option value="TM">TM (Trademark Financing)</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} mb-2`}>Message Body</label>
                    <RichTextEditor
                      value={newTextTemplate.body}
                      onChange={(value) => setNewTextTemplate({ ...newTextTemplate, body: value })}
                      placeholder="Enter message body..."
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleAddTextTemplate}
                    className="bg-teal text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal/90 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Save Template
                  </button>
                  <button
                    onClick={() => {
                      setShowAddTextForm(false);
                      setNewTextTemplate({ name: '', body: '', broker: 'All' });
                    }}
                    className={`${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer`}
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}

            {/* Text Templates List */}
            <div className="space-y-4">
              {filteredTextTemplates.map((template, index) => (
                <div key={template.id} className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-6`}>
                  {editingTextId === template.id ? (
                    <div className="space-y-4">
                      <input
                        type="text"
                        value={template.name}
                        onChange={(e) => handleChangeText(template.id, 'name', e.target.value)}
                        className={`w-full px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal font-semibold`}
                      />
                      <div>
                        <label className={`block text-sm font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} mb-2`}>Broker</label>
                        <select
                          value={template.broker || 'All'}
                          onChange={(e) => handleChangeText(template.id, 'broker', e.target.value)}
                          className={`w-full px-4 py-2.5 border ${isDarkMode ? 'border-gray-600 bg-[#394150] text-[#e6e7eb]' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-teal cursor-pointer`}
                        >
                          <option value="All">All Brokers</option>
                          <option value="NS">NS (Next Step Lending)</option>
                          <option value="TM">TM (Trademark Financing)</option>
                        </select>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-700'} mb-2`}>Message Body</label>
                        <RichTextEditor
                          value={template.body}
                          onChange={(value) => handleChangeText(template.id, 'body', value)}
                          placeholder="Enter message body..."
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleSaveText(template.id)}
                          className="bg-green-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-check-line mr-2"></i>
                          Save
                        </button>
                        <button
                          onClick={() => setEditingTextId(null)}
                          className={`${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-6 py-2.5 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3 flex-1">
                          <button
                            onClick={() => toggleTextTemplate(template.id)}
                            className={`w-8 h-8 flex items-center justify-center ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} rounded-lg transition-colors cursor-pointer`}
                          >
                            <i className={`${expandedTextTemplates.has(template.id) ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                          </button>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-[#e6e7eb]' : 'text-gray-900'}`}>{template.name}</h3>
                              {getOwnershipBadge(template)}
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getBrokerBadgeColor(template.broker || 'All')}`}>
                                {template.broker === 'NS' ? 'NS' : template.broker === 'TM' ? 'TM' : 'All Brokers'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleMoveTextTemplate(template.id, 'up')}
                            disabled={index === 0}
                            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                              index === 0 
                                ? `${isDarkMode ? 'bg-gray-800 text-gray-600' : 'bg-gray-100 text-gray-300'} cursor-not-allowed` 
                                : `${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`
                            }`}
                            title="Move Up"
                          >
                            <i className="ri-arrow-up-s-line"></i>
                          </button>
                          <button
                            onClick={() => handleMoveTextTemplate(template.id, 'down')}
                            disabled={index === filteredTextTemplates.length - 1}
                            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                              index === filteredTextTemplates.length - 1
                                ? `${isDarkMode ? 'bg-gray-800 text-gray-600' : 'bg-gray-100 text-gray-300'} cursor-not-allowed` 
                                : `${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`
                            }`}
                            title="Move Down"
                          >
                            <i className="ri-arrow-down-s-line"></i>
                          </button>
                          <button
                            onClick={() => handleEditText(template.id)}
                            className="w-9 h-9 flex items-center justify-center bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors cursor-pointer"
                            title="Edit"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button
                            onClick={() => handleDeleteText(template.id)}
                            className="w-9 h-9 flex items-center justify-center bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors cursor-pointer"
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                      {expandedTextTemplates.has(template.id) && (
                        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-4`}>
                          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} [&_p]:mb-3 [&_p:last-child]:mb-0 [&_br]:block [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_strong]:font-semibold`} dangerouslySetInnerHTML={{ __html: template.body }} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              {filteredTextTemplates.length === 0 && (
                <div className={`${isDarkMode ? 'bg-[#212936] border-gray-700' : 'bg-white border-gray-200'} rounded-xl shadow-sm border p-8 text-center`}>
                  <i className={`ri-inbox-line text-4xl ${isDarkMode ? 'text-gray-600' : 'text-gray-400'} mb-2`}></i>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>No text templates found for this filter</p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} mt-2`}>Create your first template to get started!</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
